import { Box, Stack, CircularProgress } from '@mui/material'
import { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import DataGridTable from '../dataGrid/DataGridTable'
import StatusBtn from '../atoms/status/StatusBtn'
import DropdownMenu from '../atoms/dropdown-menu/DropdownMenu'
import DeleteConfirmationModal from '../atoms/modal/DeleteConfirmationModal'
import EditTimeOffModal from '../atoms/modal/EditTimeOffModal'
import ViewTimeOffModal from '../atoms/modal/ViewTimeOffModal'
import ButtonCustom from '../atoms/button/ButtonCustom'
import styles from './styles.module.scss'
import actionStyles from './ActionButtons.module.scss'

// Debounce hook for search optimization
const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

const TimeOffTable = ({ searchTerm, staffId }) => {
	const [timeOffRequests, setTimeOffRequests] = useState([])
	const [loading, setLoading] = useState(false)
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)
	const [editModalOpen, setEditModalOpen] = useState(false)
	const [viewModalOpen, setViewModalOpen] = useState(false)
	const [selectedRequestId, setSelectedRequestId] = useState(null)
	const [selectedRequestData, setSelectedRequestData] = useState(null)
	const [actionLoading, setActionLoading] = useState({}) // Track loading state for individual buttons
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 10,
		total: 0,
		totalPages: 0
	})

	// Get user type to determine available actions
	const userType = useSelector((s) => s.auth?.user?.__t)

	// Debounce search term to avoid too many API calls
	const debouncedSearchTerm = useDebounce(searchTerm, 500)

	const fetchTimeOffRequests = useCallback(async (page = 1, limit = 10, search = '') => {
		try {
			setLoading(true)
			let url = `${process.env.REACT_APP_REQUEST_URL}/api/v2/time-management/time-off-request-list?page=${page}&limit=${limit}`
			
			// Add staffId parameter if provided (for viewing specific staff member's requests)
			if (staffId) {
				url += `&staffId=${staffId}`
			}
			
			// Add search parameter if provided
			if (search && search.trim()) {
				url += `&search=${encodeURIComponent(search.trim())}`
			}

			const response = await fetch(url, {
				method: 'GET',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			const result = await response.json()
			
			if (result.success) {
				// Transform API data to match table structure
				const transformedData = result.data.map((item) => ({
					id: item._id,
					date: new Date(item.date).toLocaleDateString('en-CA'), // Format: YYYY-MM-DD
					originalDate: item.date, // Keep original date for editing
					TimeOffType: item.timeOffType,
					ReasonForTimeOff: item.reason,
					leaveDuration: item.leaveDuration,
					status: item.status.charAt(0).toUpperCase() + item.status.slice(1), // Capitalize first letter
				}))
				
				setTimeOffRequests(transformedData)
				setPagination({
					page: result.currentPage || page,
					limit: result.limit || limit,
					total: result.total,
					totalPages: result.totalPages
				})
			}
		} catch (error) {
			console.error('Error fetching time off requests:', error)
		} finally {
			setLoading(false)
		}
	}, [staffId])

	useEffect(() => {
		fetchTimeOffRequests(1, 10, debouncedSearchTerm)
	}, [debouncedSearchTerm, staffId, fetchTimeOffRequests])

	// Handle pagination changes
	const handlePageChange = (newPage) => {
		fetchTimeOffRequests(newPage, pagination.limit, debouncedSearchTerm)
	}

	const handlePageSizeChange = (newLimit) => {
		fetchTimeOffRequests(1, newLimit, debouncedSearchTerm)
	}

	// Action handlers for dropdown menu
	const handleDelete = (id) => {
		setSelectedRequestId(id)
		setDeleteModalOpen(true)
	}

	const handleConfirmDelete = async () => {
		try {
			const response = await fetch(`${process.env.REACT_APP_REQUEST_URL}/api/v2/time-management/delete-request/${selectedRequestId}`, {
				method: 'DELETE',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
			})

			const result = await response.json()
			
			if (result.success) {
				// Refresh the table data after successful deletion
				fetchTimeOffRequests(pagination.page, pagination.limit, debouncedSearchTerm)
				toast.success('Time off request deleted successfully')
			} else {
				console.error('Failed to delete time off request:', result.message)
				toast.error('Failed to delete time off request: ' + result.message)
			}
		} catch (error) {
			console.error('Error deleting time off request:', error)
			toast.error('An error occurred while deleting the time off request')
		} finally {
			setDeleteModalOpen(false)
			setSelectedRequestId(null)
		}
	}

	const handleCancelDelete = () => {
		setDeleteModalOpen(false)
		setSelectedRequestId(null)
	}

	const handleEdit = (id) => {
		const requestData = timeOffRequests.find(request => request.id === id)
		if (requestData) {
			setSelectedRequestData(requestData)
			setEditModalOpen(true)
		}
	}

	const handleEditSuccess = () => {
		// Refresh the table data after successful edit
		fetchTimeOffRequests(pagination.page, pagination.limit, debouncedSearchTerm)
	}

	const handleCancelEdit = () => {
		setEditModalOpen(false)
		setSelectedRequestData(null)
	}

	const handleView = (id) => {
		const requestData = timeOffRequests.find(request => request.id === id)
		if (requestData) {
			setSelectedRequestData(requestData)
			setViewModalOpen(true)
		}
	}

	const handleCancelView = () => {
		setViewModalOpen(false)
		setSelectedRequestData(null)
	}

	// Approve/Reject handlers for vendors
	const handleApprove = async (id) => {
		try {
			setActionLoading(prev => ({ ...prev, [`approve-${id}`]: true }))
			
			const response = await fetch(`${process.env.REACT_APP_REQUEST_URL}/api/v2/time-management/update-request/${id}`, {
				method: 'PATCH',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ status: 'approved' })
			})

			const result = await response.json()
			
			if (result.success) {
				fetchTimeOffRequests(pagination.page, pagination.limit, debouncedSearchTerm)
				toast.success('Time off request approved successfully')
			} else {
				toast.error('Failed to approve request: ' + result.message)
			}
		} catch (error) {
			console.error('Error approving request:', error)
			toast.error('An error occurred while approving the request')
		} finally {
			setActionLoading(prev => ({ ...prev, [`approve-${id}`]: false }))
		}
	}

	const handleReject = async (id) => {
		try {
			setActionLoading(prev => ({ ...prev, [`reject-${id}`]: true }))
			
			const response = await fetch(`${process.env.REACT_APP_REQUEST_URL}/api/v2/time-management/update-request/${id}`, {
				method: 'PATCH',
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ status: 'rejected' })
			})

			const result = await response.json()
			
			if (result.success) {
				fetchTimeOffRequests(pagination.page, pagination.limit, debouncedSearchTerm)
				toast.success('Time off request rejected successfully')
			} else {
				toast.error('Failed to reject request: ' + result.message)
			}
		} catch (error) {
			console.error('Error rejecting request:', error)
			toast.error('An error occurred while rejecting the request')
		} finally {
			setActionLoading(prev => ({ ...prev, [`reject-${id}`]: false }))
		}
	}

	// Dropdown menu options based on user type
	const getDropdownOptions = (id, status) => {
		if (['kazoo-planner', 'vendor'].includes(userType)) {
			// Only kazoo-planners can approve/reject staff requests
			return [
				{
					label: 'Approve',
					icon: <img src="/images/SelectCheck.svg" alt="Approve" />,
					action: () => handleApprove(id),
					color: '#10B981'
				},
				{
					label: 'Reject',
					icon: <img src="/images/modal-close-icon.svg" alt="Reject" />,
					action: () => handleReject(id),
					color: '#EF4444'
				}
			]
		} else {
			// Staff can edit/delete their own requests (only if pending)
			const isPending = status.toLowerCase() === 'pending'
			const options = []
			
			if (isPending) {
				options.push({
					label: 'Edit',
					icon: <img src="/images/edit-icon.svg" alt="Edit" />,
					action: () => handleEdit(id)
				})
				options.push({
					label: 'Delete',
					icon: <img src="/images/trash-red.svg" alt="Delete" />,
					action: () => handleDelete(id),
					color: '#FF4444'
				})
			}
			
			return options
		}
	}

	// Remove client-side filtering since we're doing server-side search
	const displayRows = timeOffRequests

	const columns = [
		{
			field: 'date',
			headerName: 'Date',
			flex: 1,
			minWidth: 120,
			sortable: true,
			renderCell: (params) => (
				<span className="role-text">{params.value}</span>
			),
		},
		{
			field: 'TimeOffType',
			headerName: 'Time Off Type',
			flex: 1,
			minWidth: 150,
			sortable: true,
			renderCell: (params) => (
				<span className="role-text">{params.value}</span>
			),
		},
		{
			field: 'leaveDuration',
			headerName: 'Leave Duration',
			flex: 1,
			minWidth: 150,
			sortable: true,
			renderCell: (params) => (
				<span className="role-text">{params.value}</span>
			),
		},
		{
			field: 'ReasonForTimeOff',
			headerName: 'Reason For Time Off',
			flex: 1,
			minWidth: 400,
			renderCell: (params) => {
				return (
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Box component="span" sx={{ fontWeight: 500 }}>
							{params.value}
						</Box>
					</Stack>
				)
			},
		},
		{
			field: 'status',
			headerName: 'Status',
			flex: 1,
			minWidth: 130,
			renderCell: (params) => {
				const status = params.value.toLowerCase()
				let variant = 'pending'
				
				if (status === 'approved') {
					variant = 'active'  // Green color for approved
				} else if (status === 'rejected') {
					variant = 'closed'  // Red color for rejected
				}

				return (
					<StatusBtn
						label={params.value}
						variant={variant}
						className={styles.statusOption}
					/>
				)
			},
		},
		{
			field: 'actions',
			headerName: 'Actions',
			flex: 0,
			minWidth: ['kazoo-planner', 'vendor'].includes(userType) ? 180 : 80,
			sortable: false,
			renderCell: (params) => {
				if (['kazoo-planner', 'vendor'].includes(userType)) {
					// Show approve/reject buttons only for kazoo-planners
					const status = params.row.status.toLowerCase()
					const requestId = params.row.id
					const isApproveLoading = actionLoading[`approve-${requestId}`]
					const isRejectLoading = actionLoading[`reject-${requestId}`]
					
					return (
						<div className={actionStyles.actionButtonsContainer}>
							<ButtonCustom
								variant="outlined"
								size="sm"
								onClick={() => handleApprove(requestId)}
								disabled={status !== 'pending' || isApproveLoading || isRejectLoading}
								className={`${actionStyles.approveButton} ${
									status === 'approved' ? actionStyles.approved : actionStyles.pending
								} ${isApproveLoading ? actionStyles.loading : ''}`}
								style={status === 'approved' ? {
									background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
									color: '#ffffff',
									borderColor: '#10B981'
								} : {}}
							>
								{status === 'approved' ? '✓ Approved' : 'Approve'}
							</ButtonCustom>
							<ButtonCustom
								variant="outlined"
								size="sm"
								onClick={() => handleReject(requestId)}
								disabled={status !== 'pending' || isRejectLoading || isApproveLoading}
								className={`${actionStyles.rejectButton} ${
									status === 'rejected' ? actionStyles.rejected : actionStyles.pending
								} ${isRejectLoading ? actionStyles.loading : ''}`}
								style={status === 'rejected' ? {
									background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
									color: '#ffffff',
									borderColor: '#EF4444'
								} : {}}
							>
								{status === 'rejected' ? '✗ Rejected' : 'Reject'}
							</ButtonCustom>
						</div>
					)
				} else {
					// Show dropdown menu or eye button for staff
					const status = params.row.status.toLowerCase()
					const requestId = params.row.id
					const isProcessed = status === 'approved' || status === 'rejected'
					
					if (isProcessed) {
						// Show eye button for processed requests (vendor-staff can view)
						return (
							<div className={actionStyles.actionButtonsContainer}>
								<ButtonCustom
									variant="outlined"
									size="sm"
									onClick={() => handleView(requestId)}
									className={actionStyles.viewButton}
									style={{
										color: '#6B7280',
										borderColor: '#D1D5DB',
										minWidth: '40px',
										height: '32px',
										padding: '0 8px'
									}}
								>
									<img src="/images/eye-open-icon.svg" alt="View" style={{ width: '16px', height: '16px' }} />
								</ButtonCustom>
							</div>
						)
					} else {
						// Show dropdown menu for pending requests (staff can edit/delete)
						const options = getDropdownOptions(params.row.id, params.row.status)
						
						if (options.length === 0) {
							return <span style={{ color: '#9CA3AF', fontSize: '12px' }}>No actions available</span>
						}
						
						return (
							<DropdownMenu
								options={options}
								onSelect={(option) => option.action()}
							/>
						)
					}
				}
			},
		},
	]

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
				<CircularProgress />
			</Box>
		)
	}

	return (
		<div>
			<DataGridTable
				rows={displayRows}
				columns={columns}
				pagination={pagination}
				onPageChange={handlePageChange}
				onPageSizeChange={handlePageSizeChange}
				checkboxSelection={false}
				// onRowClick={handleRowClick}
			/>
			
			<DeleteConfirmationModal
				open={deleteModalOpen}
				onClose={handleCancelDelete}
				onDelete={handleConfirmDelete}
				title="Delete Time Off Request"
				message="Are you sure you want to delete this time off request? This action cannot be undone and will permanently remove the request from the system."
				confirmText="Delete Request"
				cancelText="Cancel"
			/>

			<EditTimeOffModal
				open={editModalOpen}
				onClose={handleCancelEdit}
				onSuccess={handleEditSuccess}
				requestData={selectedRequestData}
			/>

			<ViewTimeOffModal
				open={viewModalOpen}
				onClose={handleCancelView}
				requestData={selectedRequestData}
			/>
		</div>
	)
}

export default TimeOffTable